import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import styled from 'styled-components';
import { toast } from 'react-toastify';

import theme from '@styles/theme';
import { onChangeEventType } from '@utils/types';
import { CREATE_POLICY } from '@graphql/mutations';
import { Mutation } from '@graphql/types';
import { getPolicyType } from '@lib/enum';

import FullFormPage from '@components/layout/FullFormPage';
import StyledInput from '@components/styled/StyledInput';
import StyledSelect from '@components/styled/StyledSelect';
import StyledRadio from '@components/styled/StyledRadio';
import TextEditor from '@components/share/Editor/TextEditor';

const CreatePolicy = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [title, setTitle] = useState('');
    const [contents, setContents] = useState('');
    const [inputs, setInputs] = useState({
        type: '구분을 선택해주세요',
        require: ''
    });

    const [createPolicy] =
        useMutation<Pick<Mutation, 'createPolicy'>>(CREATE_POLICY);

    const onChange = (e: onChangeEventType) => {
        const { name, value } = e.target;
        setInputs((prev) => ({
            ...prev,
            [name]: value
        }));
    };
    const handleCreate = async () => {
        const { type, require } = inputs;
        if (type === '구분을 선택해주세요') {
            return toast.warning('구분을 선택해주세요');
        } else if (!require) {
            return toast.warning('필수 구분을 선택해주세요');
        } else if (!title) {
            return toast.warning('타이틀을 입력해주세요.');
        } else if (!contents) {
            return toast.warning('상세 내용을 입력해주세요.');
        }
        setLoading(true);
        try {
            await createPolicy({
                variables: {
                    type: getPolicyType(type),
                    require: require === 'true' ? true : false,
                    title,
                    text: contents
                },
                onCompleted: () => {
                    toast.success('방침 및 규약이 생성되었습니다.');
                    navigate('/operation/policy');
                }
            });
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <FullFormPage
            title="방침 및 규약 생성"
            subTitle="펫이닷 개인정보처리방침 및 이용 약관 생성"
            buttonTitle={'생성하기'}
            buttonColor={theme.colors.pointGreen}
            onSubmit={handleCreate}
            loading={loading}
        >
            <Section>
                <SectionTitle>방침 설정</SectionTitle>
                <SectionInput>
                    <StyledSelect
                        title="구분"
                        name="type"
                        value={inputs.type}
                        options={[
                            '구분을 선택해주세요',
                            '이용약관',
                            '개인정보 수집 및 이용 동의',
                            '민감정보 수집 및 이용 동의',
                            '개인정보 제3자 제공 동의',
                            '마케팅 활용 동의',
                            '광고성 정보 수신 동의'
                        ]}
                        onChange={onChange}
                        placeholder="구분을 선택해주세요"
                        description="각 방침 및 규약은 가장 마지막에 생성된 방침 기준으로 공개됩니다."
                        fontSize={15}
                        width={630}
                        COLUMN
                    />
                    <RadioArea>
                        <RadioTitle>필수 구분</RadioTitle>
                        <StyledRadio
                            name="require"
                            radioId="mandatory"
                            label="필수"
                            value="true"
                            checked={inputs.require}
                            onChange={onChange}
                            fontColor={theme.colors.blackColor}
                            labelMargin="0 12px 0 0"
                            flexOrder={2}
                            width="80px"
                        />
                        <StyledRadio
                            name="require"
                            radioId="optional"
                            label="선택"
                            value="false"
                            checked={inputs.require}
                            onChange={onChange}
                            fontColor={theme.colors.blackColor}
                            margin="0 0 0 56px"
                            labelMargin="0 12px 0 0"
                            flexOrder={2}
                            width="80px"
                        />
                    </RadioArea>
                </SectionInput>
            </Section>
            <Section>
                <SectionTitle>글 작성</SectionTitle>
                <SectionInput>
                    <StyledInput
                        title="제목"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="제목을 입력해주세요"
                        width={630}
                        maxLength={50}
                        border={`${theme.colors.ultraLightGrayBorder} 1px solid`}
                        COLUMN
                        MAX_LENGTH
                    />
                    <TextEditor
                        margin="24px 0"
                        contents={contents}
                        setContents={setContents}
                    />
                </SectionInput>
            </Section>
        </FullFormPage>
    );
};

export default CreatePolicy;

const Section = styled.section`
    display: flex;
    padding: 32px 0;
    border-top: ${theme.colors.ultraLightGrayBorder} 1px solid;
`;
const SectionTitle = styled.div`
    padding: 0 20px 0 0;
    width: 163px;
`;
const SectionInput = styled.div``;

const RadioArea = styled.div`
    display: flex;
    margin-top: 20px;
`;
const RadioTitle = styled.div`
    padding-right: 40px;
`;
